import React from 'react';
import { Tooltip } from 'react-tooltip';
import Utilities from '@util/Utilities';
import { PICKUP_WINDOWS, Pickup, PickupWindow, lastChangeAge, lastChangeDate } from './PickupCell.utilities';
export { PICKUP_WINDOWS, pickupFor } from './PickupCell.utilities';
export type { Pickup, PickupWindow } from './PickupCell.utilities';

const valueColor = (value: number) => value > 0 ? 'text-green-600' : value < 0 ? 'text-red-600' : 'text-gray-500';

const PickupValue: React.FunctionComponent<{ value: number | undefined, bold: boolean }> = ({ value, bold }) => {
	if (value === undefined) return <span className="text-gray-300">—</span>;
	return <span className={Utilities.classNames(bold ? 'font-semibold' : 'font-normal', valueColor(value))}>
		{Utilities.addPlusSign(value)}
	</span>;
};

const displayWindow = (pickup: Pickup, window: PickupWindow, bold: boolean, age: number | undefined) => <>
	<PickupValue value={pickup.byWindow[window]} bold={bold} />
	{window === 0 && pickup.byWindow[0] !== undefined && age !== undefined &&
		<span className="text-xs font-normal text-gray-500"> ({age}d)</span>}
</>;

export const PICKUP_TOOLTIP_ID = 'pickup-tooltip';

// The cell: one or two numbers, and the tooltip anchor with all four windows.
export const PickupCell: React.FunctionComponent<{
	pickup: Pickup | undefined,
	windows: PickupWindow[],
	label: string,
	className?: string,
}> = ({ pickup, windows, label, className }) => {
	// One age for the cell, the aria-label and the tooltip. The tooltip reads it from data-pickup, so the three
	// displays agree until the next render, also after local midnight.
	const age = pickup?.lastChange ? lastChangeAge(pickup.lastChange.changedAt, new Date()) : undefined;
	return <td className={Utilities.classNames('whitespace-nowrap px-2 py-1 align-middle text-sm text-center border-r-[1px] border-gray-200', className)}>
		{pickup === undefined
			? <span className="text-gray-300">—</span>
			: <span className="inline-flex cursor-default items-center gap-x-1"
							tabIndex={0}
							aria-label={`${label}: ${windows.map(window => {
								const value = pickup.byWindow[window];
								return `${window === 0 ? 'Pickup since last change' : `Pickup over ${window} days`}: ${value === undefined ? 'unavailable' : `${value > 0 ? '+' : ''}${value} rooms`}${window === 0 && age !== undefined ? ` (${age} days ago)` : ''}`;
							}).join('; ')}`}
							data-tooltip-id={PICKUP_TOOLTIP_ID}
							data-pickup={JSON.stringify({ label, age, ...pickup })}>
				{displayWindow(pickup, windows[0], true, age)}
				{windows.length > 1 && <>
					<span className="text-gray-300">·</span>
					{displayWindow(pickup, windows[1], false, age)}
				</>}
			</span>}
	</td>;
};

// One shared tooltip for the Pickup cells of the table.
export const PickupTooltip: React.FunctionComponent = () => {
	return <Tooltip
		id={PICKUP_TOOLTIP_ID}
		place="bottom"
		noArrow
		className="!bg-white shadow ring-1 !ring-black !ring-opacity-5 !rounded-md !text-left !p-0 !opacity-100 !z-50"
		render={({ activeAnchor }) => {
			const raw = activeAnchor?.getAttribute('data-pickup');
			if (raw === null || raw === undefined || raw === '') return null;
			const pickup = JSON.parse(raw) as Pickup & { label: string, age?: number };
			return <div className="w-56 px-3 py-2 text-xs">
				<p className="font-semibold text-gray-900">Pickup · {pickup.label}</p>
				<div className="mt-1 flex flex-col">
					{PICKUP_WINDOWS.map(window => <div key={window} className="flex justify-between gap-x-4 leading-5">
						<span className="text-gray-500 whitespace-nowrap">{window === 0 ? 'since last change' : `last ${window} days`}</span>
						<span className="text-right"><PickupValue value={pickup.byWindow[window]} bold />
							{window === 0 && pickup.lastChange && pickup.age !== undefined && <span className="block text-gray-500">{`${lastChangeDate(pickup.lastChange.changedAt)} (${pickup.age}d)`}</span>}
						</span>
					</div>)}
				</div>
			</div>;
		}}
	/>;
};
